import React, { FC, useRef } from "react";
import { useUploadFibiDs160 } from "../../../../../../hooks/ai/useAI";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { userDataStore } from "../../../../../../store/userData";

interface Props {
  onStart: () => void;
}

const BusinessStarter: FC<Props> = ({ onStart }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const { userData } = useSnapshot(userDataStore);
  const { mutate, isLoading } = useUploadFibiDs160();

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const temp = e.target.files[0];
      const formData = new FormData();
      formData.append("file", temp);
      mutate(formData, {
        onSuccess: () => {
          toast.success("Upload Successful!");
        },
      });
    }
  };

  const uploadedFile = userData?.userInfo.ds160URL;

  const handleViewDS160 = () => {
    if (uploadedFile) {
      window.open(uploadedFile, "_blank");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-8 pt-5 pb-10 gap-y-4">
      <p className="text-gray-600">
        Prepare for your interview with this timed 10-minute simulation. FiBi
        will provide helpful suggestions during each question, giving you
        insight into how to best respond in a professional setting. This is a
        practice environment to help you succeed.
      </p>
      <p>
        <b className="text-primaryColor">Important:</b> This simulation is 10
        minutes long, which is longer than a typical real-world interview. Once
        the simulation begins, you cannot pause or take breaks. Completing the
        entire session is required and will count towards your total practice
        sessions.
      </p>

      <div className="p-4 mb-4 border rounded-xl bg-gray-50">
        <label className="block mb-2 font-semibold text-gray-800">
          Optional: Upload Your DS-160 Form
        </label>
        <p className="mb-2 text-sm text-gray-600">
          You can upload your DS-160 form to personalize your simulation. You
          may update this before you begin.
        </p>

        <>
          {uploadedFile ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 truncate">
                Uploaded File.pdf
              </span>
              {isLoading ? (
                <span className="text-sm text-primaryColor">Uploading...</span>
              ) : (
                <div className="flex gap-6">
                  <button
                    onClick={handleViewDS160}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View File
                  </button>
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="p-2 text-sm text-white bg-primaryColor rounded-xl"
                  >
                    Change File
                  </button>
                </div>
              )}
            </div>
          ) : isLoading ? (
            <span className="text-sm text-primaryColor">Uploading...</span>
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              className="p-2 mt-2 text-sm transition-all border text-primaryColor hover:bg-blue-100 border-primaryColor rounded-xl"
            >
              Choose File
            </button>
          )}

          {/* Always in DOM, just hidden */}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
        </>
      </div>

      <button
        disabled={isLoading}
        onClick={onStart}
        className="flex items-center justify-center p-2 mt-4 text-lg text-white duration-150 px-28 bg-primaryColor rounded-xl hover:bg-Blue02 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
      >
        Start
      </button>
    </div>
  );
};

export default BusinessStarter;
